// src/Pages/Landing/BilebandiVarsinaisSuomiLanding.jsx
import React, { useMemo } from "react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import PriceInfoBox from "../../components/PriceInfoBox/PriceInfoBox.jsx";
import { trackLandingCta } from "../../utils/analytics";

/**
 * Bilebändi Varsinais-Suomi -landing React Shelliin.
 * Metat/JSON-LD tulevat WordPressistä.
 * Tavoite: selkeä hinnoittelu – bilebändien keskihinnat Varsinais-Suomessa.
 */

// Käytetään slugia, ei kovakoodattua ID:tä
const VARSUOMI_SLUG = "varsinais-suomi";

export default function BilebandiVarsinaisSuomiLanding() {
  // Paikallisen päivän ISO (YYYY-MM-DD)
  const today = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  return (
    <section className="relative max-w-6xl mx-auto px-4 pt-24 pb-24">
      {/* Hero / ingressi */}
      <header className="rounded-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-md p-8 md:p-12 shadow-lg">
        <h1 className="text-4xl md:text-6xl font-bold leading-tight text-black dark:text-white">
          Bilebändi Turkuun ja Varsinais-Suomen alueelle – vapaat kokoonpanot
        </h1>

        <p className="mt-4 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Buukkerista löydät Varsinais-Suomen vapaat bilebändit
          reaaliaikaisesti.{" "}
          <strong>
            Valitse päivämäärä, katso vapaat kokoonpanot ja pyydä tarjous
            parilla klikkauksella.
          </strong>
        </p>

        <p className="mt-3 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Tarjottu hinta on <strong>kokonaishinta (sis. matkakulut)</strong>.
          Lisäksi esiintyjältä voi tilata tai ostaa lisäpalveluita tarpeen
          mukaan.
        </p>

        {/* CTA-napit */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <a
            href={`/?type=bilebandi&maakunta=${VARSUOMI_SLUG}&date=${today}&clear=1#tulokset`}
            aria-label="Näytä tämän päivän vapaat (Varsinais-Suomi)"
            onClick={() =>
              trackLandingCta({
                actType: "bilebandi",
                region: VARSUOMI_SLUG,
                placement: "hero_today_free",
              })
            }
          >
            <NeonButton variant="primary" size="md" className="font-semibold">
              Näytä tämän päivän vapaat (Varsinais-Suomi)
            </NeonButton>
          </a>

          <a
            href={`/?type=bilebandi&book=1&maakunta=${VARSUOMI_SLUG}&clear=1#tulokset`}
            aria-label="Avaa pikahaku"
            onClick={() =>
              trackLandingCta({
                actType: "bilebandi",
                region: VARSUOMI_SLUG,
                placement: "hero_quick_search",
              })
            }
          >
            <NeonButton variant="secondary" size="md" className="font-semibold">
              Avaa pikahaku
            </NeonButton>
          </a>
        </div>
      </header>

      {/* Hintainfoboksi: Varsinais-Suomen bilebändien keskihinta */}
      <div className="mt-8">
        <PriceInfoBox
          title="Bilebändien keskihinnat Varsinais-Suomessa – suosituimmat setit"
          types={["bilebandi"]}
          autoTopSets
          topN={3}
          regionSlug={VARSUOMI_SLUG} // käytä Varsinais-Suomen hintoja
          refreshMs={15 * 60 * 1000}
          compact={true}
          nationalLabel="Buukkerin keskihinta"
          showRegionDetails={false} // vain iso luku + teksti, ei Min–Max / Havaintoja
        />

        <p className="mt-2 text-black/80 dark:text-white/80 text-sm opacity-70">
          Hinta on suuntaa-antava keskiarvo Varsinais-Suomesta (esimerkiksi
          Turku, Loimaa ja lähialueet).{" "}
          <strong>Lopullinen hinta täsmentyy tarjouspyynnössä</strong> ja se
          sisältää myös matkakulut. Hinnat perustuvat Buukkerin bilebändien
          hinnastoihin, joissa{" "}
          <strong>
            Varsinais-Suomi on merkitty keikkailualueeksi tai bändin
            kotipaikaksi
          </strong>{" "}
          – ei pelkästään yksittäiseen kaupungin nimeen.
        </p>
      </div>

      {/* Ristiinlinkitys */}
      <div className="mt-8 rounded-2xl p-6 md:p-8 bg-white/70 dark:bg-zinc-900/60 ring-1 ring-black/10 dark:ring-white/10 shadow-lg">
        <p className="text-black/80 dark:text-white/80 text-base md:text-lg">
          Tarvitsetko DJ:tä tai trubaduuria Varsinais-Suomessa? Siirry{" "}
          <a className="underline" href="/dj-varsinais-suomi/">
            DJ Varsinais-Suomi
          </a>{" "}
          tai{" "}
          <a className="underline" href="/trubaduuri-varsinais-suomi/">
            Trubaduuri Varsinais-Suomi
          </a>{" "}
          -sivulle. Lue myös:{" "}
          <a
            className="underline"
            href="https://buukkeri.fi/blogi/nain-loydat-taydellisen-esiintyjan-juhliisi"
          >
            Näin löydät täydellisen esiintyjän juhliisi
          </a>
          .
        </p>
      </div>
    </section>
  );
}
